import React, { useEffect, useState } from "react";
import { ShieldCheck, CheckCircle, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../lib/teleproApi";

/**
 * Modal bloquante pour la signature de l'avenant — cahier de modifs 2026-02.
 *
 * Cible uniquement les 2 signataires historiques (Carine, Léa). À la prochaine
 * connexion, on leur affiche l'avenant en modal bloquante avant l'accès au
 * portail. Le contrat initial est CONSERVÉ (jamais écrasé) — seule la trace
 * de l'avenant est ajoutée sur le doc telepro.
 *
 * Props:
 *   - onSigned : callback appelé après signature réussie (pour refresh /me).
 */
export default function AvenantModal({ onSigned }) {
  const [data, setData] = useState(null);
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    teleproApi.get("/me/avenant")
      .then((r) => setData(r.data))
      .catch(() => setData({ needs_signature: false }));
  }, []);

  const sign = async () => {
    if (!accept) return;
    setSubmitting(true);
    try {
      await teleproApi.post("/me/avenant/sign", { accept });
      toast.success("Avenant signé électroniquement. Merci !");
      onSigned && onSigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signature impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await teleproApi.get("/me/avenant.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "avenant-contrat.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Téléchargement impossible.");
    } finally {
      setDownloading(false);
    }
  };

  if (!data || !data.needs_signature) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="avenant-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="avenant-title"
    >
      <div className="bg-card border border-primary/40 rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-primary text-primary-foreground px-6 py-4 flex items-center gap-3 z-10">
          <ShieldCheck size={22} weight="duotone"/>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Signature requise</div>
            <h2 id="avenant-title" className="font-display font-semibold text-lg leading-tight">
              Avenant au contrat de téléprospection
            </h2>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-secondary/10 border-l-4 border-secondary rounded-sm p-3 mb-4 text-xs leading-relaxed">
            Bonjour <b>{data.partner?.first_name}</b>, un <b>avenant</b> complète ton contrat initial
            (qui reste conservé tel quel). Merci de le lire et de le signer électroniquement pour continuer.
            Ta signature est enregistrée avec horodatage, IP et navigateur — même valeur juridique qu'une
            signature manuscrite.
          </div>

          <div className="bg-card border border-border rounded-sm divide-y divide-border mb-4"
               data-testid="avenant-articles">
            {(data.articles || []).map((a, i) => (
              <div key={i} className="p-4">
                <div className="font-display font-semibold text-primary text-sm mb-1">
                  {i + 1}. {a.title}
                </div>
                <div className="text-xs leading-relaxed text-foreground/90 whitespace-pre-line">
                  {a.body}
                </div>
              </div>
            ))}
          </div>

          <label className="flex items-start gap-2 text-xs cursor-pointer mb-4 bg-muted/30 p-3 rounded-sm">
            <input
              type="checkbox"
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
              className="mt-0.5"
              data-testid="avenant-accept"
            />
            <span>
              Je reconnais avoir <b>lu, compris et accepté</b> l&apos;intégralité du présent avenant.
              Je confirme que mon contrat initial reste applicable et que l&apos;avenant le complète.
            </span>
          </label>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={sign}
              disabled={!accept || submitting}
              className="flex-1 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-3 rounded-sm text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="avenant-sign-btn"
            >
              <CheckCircle size={14} weight="bold"/>
              {submitting ? "Signature en cours…" : "Signer électroniquement l'avenant"}
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloading}
              className="bg-card border border-border hover:bg-accent px-4 py-3 rounded-sm text-xs font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="avenant-download-pdf"
            >
              <FilePdf size={13} weight="bold"/>
              {downloading ? "Préparation…" : "Aperçu PDF"}
            </button>
          </div>

          <p className="text-[10px] text-muted-foreground mt-3 text-center">
            Avenant version {data.version}
          </p>
        </div>
      </div>
    </div>
  );
}
